import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../api';
import ThemeToggle from './ThemeToggle';
import { BarChart2, Users, List, Package, Settings, ArrowLeft, LogOut, Menu, X, MessageCircle, CheckSquare, Bell } from 'react-feather';
import { useEffect, useState } from 'react';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import BrandWordmark from './BrandWordmark';
import { ADMIN_PERMISSION_DEFAULTS, canAccessAdminArea } from '../utils/adminPermissions';
import { formatCurrencyWithConversion } from '../utils/currency';
import './AdminLayout.css';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: BarChart2, end: true, area: 'dashboard' },
  { to: '/admin/users', label: 'Users', icon: Users, area: 'users' },
  { to: '/admin/orders', label: 'Orders', icon: List, area: 'orders' },
  { to: '/admin/support', label: 'Support', icon: MessageCircle, area: 'support' },
  { to: '/admin/services', label: 'Services', icon: Package, area: 'services' },
  { to: '/admin/service-testing', label: 'Service Testing', icon: CheckSquare, area: 'service_testing' },
  { to: '/admin/settings', label: 'Settings', icon: Settings, area: 'settings' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [permissions, setPermissions] = useState(ADMIN_PERMISSION_DEFAULTS);
  const { unreadCount } = useUnreadMessages();

  useEffect(() => {
    adminAPI.settings()
      .then((response) => setPermissions(prev => ({ ...prev, ...response.data.settings })))
      .catch(() => setPermissions(ADMIN_PERMISSION_DEFAULTS));
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };
  const visibleNavItems = navItems.filter((item) => canAccessAdminArea(user, permissions, item.area));

  return (
    <div className="admin-layout">
      {/* Mobile top nav bar */}
      <div className="admin-mobile-topnav">
        <button className="admin-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="admin-mobile-logo">
          <BrandWordmark className="admin-logo-text" />
        </div>
        <div className="admin-mobile-topnav-controls">
          <button 
            className="notification-bell-mobile" 
            onClick={() => navigate('/admin/support')}
            title={`Support messages (${unreadCount})`}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          <ThemeToggle />
        </div>
      </div>

      {/* Desktop top nav bar */}
      <div className="admin-desktop-topnav">
        <div className="admin-topnav-controls">
          <button 
            className="notification-bell" 
            onClick={() => navigate('/admin/support')}
            title={`Support messages (${unreadCount})`}
            style={{ position: 'relative' }}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          <ThemeToggle />
        </div>
      </div>

      {/* Admin Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Logo */}
        <div className="admin-sidebar-logo">
          <BrandWordmark className="admin-logo-text" />
        </div>

        {/* Admin badge */}
        <div className="admin-badge">{user?.role === 'super_admin' ? 'Super Admin Panel' : 'Admin Panel'}</div>

        {/* Nav */}
        <nav className="admin-sidebar-nav">
          {visibleNavItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>

        {/* Switch to User Panel */}
        <NavLink to="/dashboard" className="sidebar-link admin-user-panel-link" onClick={() => setSidebarOpen(false)}>
          <ArrowLeft size={18} /> User Panel
        </NavLink>

        {/* User info */}
        <div className="admin-sidebar-user">
          <div className="admin-user-avatar">{user?.username?.[0]?.toUpperCase()}</div>
          <div className="admin-user-info">
            <div className="admin-user-header">
              <div className="admin-user-name">{user?.username}</div>
              <div className="admin-user-balance">{formatCurrencyWithConversion(user?.balance || 0, user?.currency || 'USD')}</div>
            </div>
            <div className="admin-user-role">{user?.role === 'super_admin' ? 'Super Administrator' : 'Administrator'}</div>
          </div>
        </div>

        {/* Logout button */}
        <button 
          className="admin-logout-btn"
          onClick={handleLogout}
          title="Logout"
        >
          <LogOut size={18} /> Logout
        </button>
      </aside>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="admin-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <main className="admin-main-content">
        <Outlet />
      </main>
    </div>
  );
}
